import axios, { type AxiosError } from 'axios'
import {
    useQuery,
    keepPreviousData,
    useQueryClient,
    useMutation,
} from '@tanstack/react-query'
import { useAtomValue } from "jotai"

import {
    type FormMutationOptions,
    type MutationOptions,
    type YearlyFeePayerFormInput,
    type YearlyFeePayerSingle,
    type Pagination,
    type YearlyFeePayerListFilters,
    type YearlyFeePayersResponse,
    MMParticipation,
    type HouseholdMemberCustomDiscountFormInput,
} from '@/types'
import { convertToCents, isErrorResponse } from '@/helpers'

export function useYearlyFeePayers(
    yearlyFeeCollectionId: number,
    {
        pagination,
        filters,
    }: {
        pagination: Pagination,
        filters: YearlyFeePayerListFilters,
    }
) {
    return useQuery({
        queryKey: [
            'yearlyFeePayers',
            `yearlyFeeCollection-${yearlyFeeCollectionId}`,
            `limit-${pagination.limit}`,
            `offset-${pagination.offset}`,
            `householdId-${filters.householdId}`,
            `discountId-${filters.discountId}`,
            `nameLike-${filters.nameLike}`,
            `variableSymbolLike-${filters.variableSymbolLike}`,
            'allPaymentsPaid-'+filters.allPaymentsPaid,
        ],
        queryFn: async () => {
            const queryParams = new URLSearchParams()

            queryParams.append('yearly_fee_collection_id', yearlyFeeCollectionId.toString())
            queryParams.append('limit', pagination.limit.toString())
            queryParams.append('offset', pagination.offset.toString())
            if (filters.householdId) {
                queryParams.append('household_id', filters.householdId.toString())
            }
            if (filters.discountId) {
                queryParams.append('discount_id', filters.discountId.toString())
            }
            if (filters.nameLike) {
                queryParams.append('name_like', filters.nameLike)
            }
            if (filters.variableSymbolLike) {
                queryParams.append('variable_symbol_like', filters.variableSymbolLike)
            }
            if (filters.allPaymentsPaid === MMParticipation.yes || filters.allPaymentsPaid === MMParticipation.no) {
                queryParams.append('all_payments_paid', filters.allPaymentsPaid === MMParticipation.yes ? 'true' : 'false')
            }
            
            const { data } : { data: YearlyFeePayersResponse } = await axios.get(
                `/api/fee_payer/list?${queryParams.toString()}`
            )
            return data
        },
        placeholderData: keepPreviousData,
    })
}

export function useYearlyFeePayer(yearlyFeePayerId) {
    return useQuery({
        queryKey: ['yearlyFeePayer', yearlyFeePayerId],
        queryFn: async () => {
            const { data } : { data: YearlyFeePayerSingle } = await axios.get(
                '/api/fee_payer/single?id=' + yearlyFeePayerId,
            )
            return data
        },
    })
}

export const useGenerateYearlyFeePayerFromScratch = (yearlyFeeCollectionId: number, options: FormMutationOptions) => {
    const queryClient = useQueryClient()

    const {
        onSuccessCallback,
        formDataId,
        setRequestInProgress,
        onErrorCallback,
    } = options

    return useMutation({
        mutationFn: (data: YearlyFeePayerFormInput) => {            
            const queryParams = new URLSearchParams()
            if (yearlyFeeCollectionId) {
                queryParams.append('yearly_fee_collection_id', yearlyFeeCollectionId.toString())
            }
            queryParams.append('name', data.name)
            queryParams.append('requested_amount_cents', convertToCents(data.requested_amount_cents).toString())
            queryParams.append('collection_period', data.collection_period)
            queryParams.append('payer_type', data.payer_type)
            queryParams.append('email_address', data.email_address)
            queryParams.append('phone_number', data.phone_number)
            queryParams.append('account_number', data.account_number)
            queryParams.append('variable_symbol', data.variable_symbol.toString())

            if (data.household_id) {
                queryParams.append('household_id', data.household_id.toString())
            }

            return axios.post(`/api/fee_payer/generate_from_sketch?${queryParams.toString()}`)
        },
        onSuccess: response => {
            queryClient.invalidateQueries({ queryKey: ['yearlyFeePayers'] })
            localStorage.removeItem(formDataId)
            if (onSuccessCallback) {
                onSuccessCallback(response.data)
            }
        },
        onSettled: () => {
            setRequestInProgress(false)
        },
        onError: (axiosResponse: AxiosError) => {
            const errorData = axiosResponse.response?.data
            if (onErrorCallback){
                if (isErrorResponse(errorData)) {
                    onErrorCallback(errorData.error)
                } else {
                    console.error(axiosResponse)
                    onErrorCallback('Došlo k neočekávané chybě.');
                }
            }
        }
    })
}

export const useUpdateYearlyFeePayer = (yearlyFeePayerId: number, options: FormMutationOptions) => {
    const queryClient = useQueryClient()

    const {
        onSuccessCallback,
        formDataId,
        setRequestInProgress,
        onErrorCallback,
    } = options

    return useMutation({
        mutationFn: (data: YearlyFeePayerFormInput) => {
            const queryParams = new URLSearchParams()
            queryParams.append('id', yearlyFeePayerId.toString())
            queryParams.append('name', data.name)
            queryParams.append('requested_amount_cents', convertToCents(data.requested_amount_cents).toString())
            queryParams.append('collection_period', data.collection_period)
            queryParams.append('payer_type', data.payer_type)
            queryParams.append('email_address', data.email_address)
            queryParams.append('phone_number', data.phone_number)
            queryParams.append('account_number', data.account_number)
            queryParams.append('variable_symbol', data.variable_symbol.toString())

            return axios.put(`/api/fee_payer/update?${queryParams.toString()}`)
        },
        onSuccess: response => {
            queryClient.invalidateQueries({ queryKey: ['yearlyFeePayers'] })
            queryClient.invalidateQueries({ queryKey: ['yearlyFeePayer', yearlyFeePayerId] })
            localStorage.removeItem(formDataId)
            if (onSuccessCallback) {
                onSuccessCallback(response.data)
            }
        },
        onSettled: () => {
            setRequestInProgress(false)
        },
        onError: (axiosResponse: AxiosError) => {
            const errorData = axiosResponse.response?.data
            if (onErrorCallback){
                if (isErrorResponse(errorData)) {
                    onErrorCallback(errorData.error)
                } else {
                    console.error(axiosResponse)
                    onErrorCallback('Došlo k neočekávané chybě.');
                }
            }
        }
    })
}

export const useYearlyFeePayerAssignDiscount = (yearlyFeePayerId, options: MutationOptions) => {
    const queryClient = useQueryClient()

    const {
        setRequestInProgress,
        onErrorCallback,
        onSuccessCallback,
    } = options

    return useMutation({
        mutationFn: (discountId: number | string) => {
            return axios.post(`/api/fee_payer/assign_discount?id=${yearlyFeePayerId
                }&discount_id=${discountId}`)
        },
        onSuccess: response => {
            const data = response.data
            queryClient.invalidateQueries({ queryKey: ['yearlyFeePayer', yearlyFeePayerId] })
            if (!data.errors || data.errors.length === 0) {
                onSuccessCallback('Sleva aplikována.')
            } else {
                onErrorCallback(data.errors.join(', '))
            }
        },
        onSettled: () => {
            setRequestInProgress(false)
        },
        onError: (axiosResponse: AxiosError) => {
            const errorData = axiosResponse.response?.data
            if (onErrorCallback){
                if (isErrorResponse(errorData)) {
                    onErrorCallback(errorData.error)
                } else {
                    console.error(axiosResponse)
                    onErrorCallback('Došlo k neočekávané chybě.');
                }
            }
        }
    })
}

export const useYearlyFeePayerUnassignDiscount = (yearlyFeePayerId: number | string, discountId: number | string, options: MutationOptions,) => {
    const queryClient = useQueryClient()

    const {
        onSuccessCallback,
        setRequestInProgress,
    } = options

    return useMutation({
        mutationFn: () => {
            return axios.post(`/api/fee_payer/unassign_discount?id=${yearlyFeePayerId}&discount_id=${discountId}`)
        },
        onSuccess: response => {
            queryClient.invalidateQueries({ queryKey: ['yearlyFeePayer', yearlyFeePayerId] })
            if (onSuccessCallback) {
                onSuccessCallback(response.data)
            }
        },
        onSettled: () => {
            setRequestInProgress(false)
        }
    })
}

export const useCreateHouseholdMemberCustomDiscount = (feePayerId: number | string, options: FormMutationOptions) => {
    const queryClient = useQueryClient()

    const {
        onSuccessCallback,
        formDataId,
        setRequestInProgress,
        onErrorCallback,
    } = options

    return useMutation({
        mutationFn: (data: HouseholdMemberCustomDiscountFormInput) => {            
            const queryParams = new URLSearchParams()
            queryParams.append('id', feePayerId.toString())
            queryParams.append('name', data.name)
            queryParams.append('amount_cents', data.amount ? convertToCents(data.amount).toString() : '')
            queryParams.append('desription', data.description)

            return axios.post(`/api/fee_payer/create_household_member_custom_discount?${queryParams.toString()}`)
        },
        onSuccess: response => {
            queryClient.invalidateQueries({ queryKey: ['yearlyFeePayer', feePayerId] })
            queryClient.invalidateQueries({ queryKey: ['yearlyFeeDiscounts'] })
            localStorage.removeItem(formDataId)
            if (onSuccessCallback) {
                onSuccessCallback(response.data)
            }
        },
        onSettled: () => {
            setRequestInProgress(false)
        },
        onError: (axiosResponse: AxiosError) => {
            const errorData = axiosResponse.response?.data
            if (onErrorCallback){
                if (isErrorResponse(errorData)) {
                    onErrorCallback(errorData.error)
                } else {
                    console.error(axiosResponse)
                    onErrorCallback('Došlo k neočekávané chybě.');
                }
            }
        }
    })
}

export const useCreateHouseholdCustomDiscount = (feePayerId: number | string, options: FormMutationOptions) => {
    const queryClient = useQueryClient()

    const {
        onSuccessCallback,
        formDataId,
        setRequestInProgress,
        onErrorCallback,
    } = options

    return useMutation({
        mutationFn: (data: HouseholdMemberCustomDiscountFormInput) => {            
            const queryParams = new URLSearchParams()
            queryParams.append('id', feePayerId.toString())
            queryParams.append('name', data.name)
            queryParams.append('amount_cents', data.amount ? convertToCents(data.amount).toString() : '')
            queryParams.append('desription', data.description)

            return axios.post(`/api/fee_payer/create_household_custom_discount?${queryParams.toString()}`)
        },
        onSuccess: response => {
            queryClient.invalidateQueries({ queryKey: ['yearlyFeePayer', feePayerId] })
            queryClient.invalidateQueries({ queryKey: ['yearlyFeeDiscounts'] })
            localStorage.removeItem(formDataId)
            if (onSuccessCallback) {
                onSuccessCallback(response.data)
            }
        },
        onSettled: () => {
            setRequestInProgress(false)
        },
        onError: (axiosResponse: AxiosError) => {
            const errorData = axiosResponse.response?.data
            if (onErrorCallback){
                if (isErrorResponse(errorData)) {
                    onErrorCallback(errorData.error)
                } else {
                    console.error(axiosResponse)
                    onErrorCallback('Došlo k neočekávané chybě.');
                }
            }
        }
    })
}
